import type { RefObject } from 'react';
import type { WalletProps } from '../hooks/useWalletsProps';

export { detectBrowser, detectOS, isAndroid, isIOS, isMobile } from './detect';

const truncateRegex = /^(0x[a-zA-Z0-9]{4})[a-zA-Z0-9]+([a-zA-Z0-9]{4})$/;

export const defaultTokenIcon = 'https://static.particle.network/token-list/defaultToken/default.png';

interface ElementDimensions {
  width: number;
  height: number;
  topDistance: number;
  bottomDistance: number;
}

export const truncateEthAddress = (address?: string, separator = '••••') => {
  if (!address) return '';
  const match = address.match(truncateRegex);
  if (!match) return address;
  return `${match[1]}${separator}${match[2]}`;
};

export const isWalletConnectConnector = (connectorId?: string) => connectorId === 'walletConnect';

export function hexToRgba(hex: string, alpha: number): string {
  if (!hex) return '';
  hex = hex.replace(/^#/, '');

  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((c) => c + c)
      .join('');
  }

  if (hex.length !== 6) return '';

  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export const shortAddress = (address: string | undefined | null) => {
  if (!address) {
    return '';
  }
  if (address.length <= 10) {
    return address;
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
};

/**
 * Copy text to clipboard
 * @param {string} text
 * @returns {Promise<void>}
 */
export async function copyToClipboard(text: string) {
  const clipboardCopy = async () => {
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);

      textarea.select();
      const result = document.execCommand('copy');

      document.body.removeChild(textarea);

      if (!result) {
        throw new Error('Copy to clipboard failed');
      }
    }
  };

  return new Promise((resolve, reject) => {
    clipboardCopy().then(resolve).catch(reject);
  });
}

export const openMobileUrl = (url: string) => {
  if (url.startsWith('http')) {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noreferrer noopener';
    link.click();
  } else {
    window.location.href = url;
  }
};

export const hasPCDownloadUrl = (walletProps: WalletProps) => {
  return Boolean(
    walletProps.downloadUrls?.chrome ||
      walletProps.downloadUrls?.brave ||
      walletProps.downloadUrls?.edge ||
      walletProps.downloadUrls?.firefox ||
      walletProps.downloadUrls?.safari
  );
};

export function getElementDimensionsRelativeToParent(ref: RefObject<HTMLElement | null>): ElementDimensions | null {
  if (!ref.current) {
    return null;
  }

  const element = ref.current;
  const parent = element.parentElement;

  if (!parent) {
    return null;
  }

  const elementRect = element.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  const topDistance = elementRect.top - parentRect.top;
  const bottomDistance = parentRect.bottom - elementRect.bottom;

  const dimensions = {
    width: elementRect.width,
    height: elementRect.height,
    topDistance: topDistance,
    bottomDistance: bottomDistance,
  };

  return dimensions;
}

export function getBuyUrl(
  chainId?: number,
  address?: string,
  currency = 'USD',
  theme = 'light',
  language = 'en-US'
): string {
  const RAMP_WEB_URL = 'https://ramp.particle.network';

  const supportedNetworks: { [key: string]: string[] } = {
    '1': ['Ethereum', 'ETH'],
    '10': ['Optimism', 'ETH'],
    '56': ['Binance Smart Chain', 'BNB'],
    '101': ['Solana', 'SOL'],
    '137': ['Polygon', 'MATIC'],
    '204': ['opBNB', 'BNB'],
    '324': ['Zksync', 'ETH'],
    '5000': ['Mantle', 'MNT'],
    '8453': ['Base', 'ETH'],
    '42161': ['Arbitrum One', 'ETH'],
    '42220': ['Celo', 'CELO'],
    '43114': ['Avalanche', 'AVAX'],
    '728126428': ['Tron', 'TRX'],
    // sui TODO
  };

  if (!chainId || !address || !supportedNetworks[chainId]) {
    return '';
  }

  const chainName = supportedNetworks[chainId]?.[0] || '';

  const cryptoCoin = supportedNetworks[chainId]?.[1] || 'ETH';

  const buyParams = {
    walletAddress: address,
    fiatCoin: currency,
    cryptoCoin: cryptoCoin,
    chainName: chainName,
    showBackButton: false,
    theme: theme,
    language: language,
  };

  const queryString = new URLSearchParams(buyParams as any).toString();
  return `${RAMP_WEB_URL}/?${queryString}`;
}

export function popupWindow(url: string, title: string, w: number, h: number): Window | null {
  const left = screen.width / 2 - w / 2;
  const top = screen.height / 2 - h / 2;
  return window.open(
    url,
    title,
    'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=' +
      w +
      ', height=' +
      h +
      ', top=' +
      top +
      ', left=' +
      left
  );
}

export const roundToEven = (value: number) => {
  return Math.floor(value / 2) * 2;
};

/**
 * Judge whether the user is registering a passkey for the first time
 * @returns {boolean} True if it's the first time, false otherwise
 */
export const isFirstRegisterPasskey = () => {
  return localStorage.getItem('passkeyRegistrationStatus') !== 'completed';
};

/**
 * Set that the user has completed passkey registration
 */
export const setFirstRegisterPasskey = () => {
  localStorage.setItem('passkeyRegistrationStatus', 'completed');
};
